import React, { useState, useRef, useEffect } from "react";
import { Container, InputGroup, Form, Button, Row, Col, Tab, Nav, ButtonGroup, Modal, Tooltip, Dropdown } from 'react-bootstrap';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";

import ReactSlider from "react-slider";
import { useProfile } from "../contexts/ProfileContext"; // Import the useProfile hook
import axios from "axios";

import ChartComponent from "./FollowerChart";
import RegisterSocial from "./RegisterSocial";
import Upgrade from "./Upgrade";
import Booster from "./Booster";
import Tools from "./Tools";
import Upsell from "./Upsell";
import { useLocation } from "react-router-dom";
import Alert from "./Alert";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";

// Country targeting is hidden in the UI. selectedCountries still hydrates and
// still ships in the save payload, so existing per-profile selections are
// preserved untouched — flip this to true to bring the picker back.
const SHOW_COUNTRY_TARGETING = false;

const Magnet = () => {
	const navigate = useNavigate();
  const L = useLanguage();
  const profile = useProfile(); // Retrieve profile from context
  const [selectedInterests, setSelectedInterests] = useState([]); // Initialize as an empty array
  const [selectedCountries, setSelectedCountries] = useState([]); // Initialize with default country
  const [ageRange, setAgeRange] = useState([18, 65]); // Default min and max age
  const [gender, setGender] = useState("both");
  // Only auto-save targeting settings AFTER the user actually edits them. Without
  // this gate the auto-save effect fires once on mount with the blank useState
  // defaults (interests=[], countries=[]) and races the real hydrated write,
  // intermittently nulling the user's saved interests/countries.
  const userEditedRef = useRef(false);
  const [username, setUsername] = useState(profile?.socials?.[profile?.selectedProfile]?.username || "");
  const [followers, setFollowers] = useState(profile?.socials?.[profile?.selectedProfile]?.followers || 0);
  const [lastDayChange, setLastDayChange] = useState(profile?.socials?.[profile?.selectedProfile]?.lastDayChange || 0);
  const [lastWeekChange, setLastWeekChange] = useState(profile?.socials?.[profile?.selectedProfile]?.lastWeekChange || 0);
  const [lastMonthChange, setLastMonthChange] = useState(profile?.socials?.[profile?.selectedProfile]?.lastMonthChange || 0);
  const [platform, setPlatform] = useState(profile?.socials?.[profile?.selectedProfile]?.platform || "");
  const [selectedProfile, setSelectedProfile] = useState(profile.selectedProfile || "");
  const [photo, setPhoto] = useState(profile?.socials?.[profile?.selectedProfile]?.photo || "/images/default.png");
  const [currentKey, setCurrentKey] = useState("overview");
  const defaultPhoto = "/images/default.png";
  
  // State to manage the growth rate (10 or 100 followers/day)
  const [growthRate, setGrowthRate] = useState("0");
  const [showProModal, setProModal] = useState(false);
  const handleCloseModal = () => setProModal(false);
  const handleShowModal = () => setProModal(true);

  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const handleCloseUpsellModal = () => setShowUpsellModal(false);
  const handleShowUpsellModal = () => setShowUpsellModal(true);

  const [isPaused, setIsPaused] = useState(profile?.socials?.[profile?.selectedProfile]?.isPaused === 'true');
  const [isPauseLoading, setIsPauseLoading] = useState(false);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);

  const [functionError, setFunctionError] = useState("");
  const [functionSuccess, setFunctionSuccess] = useState("");

  const handleTogglePause = async () => {
    if (!isPaused) {
      setShowPauseConfirm(true);
      return;
    }
    await executePauseToggle();
  };

  const executePauseToggle = async () => {
    setShowPauseConfirm(false);
    setIsPauseLoading(true);
    try {
      const newPaused = !isPaused;
      await axios.post("https://api.viralgpt.com/auth/profile/pause", {
        profileid: profile.selectedProfile,
        paused: newPaused
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` }
      });
      setIsPaused(newPaused);
      if (newPaused) {
        setFunctionSuccess(L.magnet.growthPausedMsg);
      } else {
        setFunctionSuccess(L.magnet.growthResumedMsg);
      }
    } catch (e) {
      setFunctionError(e.response?.data?.error || L.magnet.failedUpdateGrowth);
    } finally {
      setIsPauseLoading(false);
    }
  };

  const [days, setDays] = useState(30);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const modeParam = queryParams.get("mode");

  // The Follower Magnet is what a membership buys, so non-members never see its
  // tab — a one-time package gets them into the dashboard, not into the magnet.
  // A one-time purchase leaves a canceled subscription row with a future
  // endDate, so this reads the user-level flag rather than the profile status
  // (which the API reports as 'active' for the paid period either way).
  const hasMembership = profile?.hasActiveSubscription === true;

  // Magnet and AI Tools are legacy-plan features. A one-time buyer gets Instant
  // Boosts only — that is what their purchase bought.
  const validModes = hasMembership ? ["overview", "boost", "tools"] : ["boost"];
  var defaultKey = validModes.includes(modeParam) ? modeParam : validModes[0];

  useEffect(() => {
    const newUrl = new URL(window.location);
    newUrl.searchParams.set("mode", defaultKey);
    window.history.pushState({}, "", newUrl);
  }, [defaultKey]);

  // The profile arrives after first paint, so re-check once it lands: anyone
  // sitting on the magnet tab without a membership moves to Extra Boosts.
  useEffect(() => {
    if (!hasMembership && (currentKey === "overview" || currentKey === "tools")) {
      setCurrentKey("boost");
    }
  }, [hasMembership, currentKey]);
  
  const changeTab = (tab) => {
    const newUrl = new URL(window.location);
    newUrl.searchParams.set("mode", tab);
    window.history.pushState({}, "", newUrl);
	setCurrentKey(tab);
  };

  
  // State for turning the Follower Magnet on/off
  const [followerMagnetOn, setFollowerMagnetOn] = useState(true);

  const [isShaking, setIsShaking] = useState("none");


  useEffect(() => {
    // Set growth rate based on profile's plan
    if (profile?.socials?.[profile?.selectedProfile]?.planName === 'pro' && profile?.socials?.[profile?.selectedProfile]?.status != 'skipped' && profile?.socials?.[profile?.selectedProfile]?.status != 'canceled') {
      setGrowthRate("100");
    } else if (profile?.socials?.[profile?.selectedProfile]?.planName === 'basic' && profile?.socials?.[profile?.selectedProfile]?.status != 'skipped' && profile?.socials?.[profile?.selectedProfile]?.status != 'canceled') {
      setGrowthRate("50");
    } else if (profile?.socials?.[profile?.selectedProfile]?.planName === 'starter' && profile?.socials?.[profile?.selectedProfile]?.status != 'skipped' && profile?.socials?.[profile?.selectedProfile]?.status != 'canceled') {
      setGrowthRate("7");
    }
  }, [profile?.socials?.[profile?.selectedProfile]?.planName, profile?.socials?.[profile?.selectedProfile]?.status]);
  const handleProClick = async () => {

	if (profile?.socials?.[profile?.selectedProfile]?.status == 'skipped' || profile?.socials?.[profile?.selectedProfile]?.status == 'canceled') {
		navigate('/dashboard/settings?tab=invoices');
	}
    if (profile?.socials?.[profile?.selectedProfile]?.planName === 'pro') {
      setGrowthRate("100");
    } else {
      // Trigger shake animation
      setIsShaking("pro");
      
      // Reset the shaking state after the animation duration and then open the modal
      setTimeout(() => {
        setIsShaking("none");
        handleShowModal();
      }, 500); // 500ms matches the CSS animation duration
    }
  };

  const handleBasicClick = async () => {
    if (profile?.socials?.[profile?.selectedProfile]?.planName != 'basic' && profile?.socials?.[profile?.selectedProfile]?.planName != 'pro') {
      setIsShaking("basic");
	  setTimeout(() => {
        setIsShaking("none");
        handleShowModal();
      }, 500); // 500ms matches the CSS animation duration
    }
  };

  const selectProfile = (uuid) => {
    setSelectedProfile(uuid);
	axios.post("https://api.viralgpt.com/auth/select", {
		profileid: uuid
	}, {
		headers: {
			Authorization: `Bearer ${localStorage.getItem("accessToken")}`
		}
	})
	.then(response => {
		window.location.reload();
	})
	.catch(error => {
		console.error("Error selecting profile:", error);
	});
  };
  const [showRegisterSocial, setShowRegisterSocial] = useState(false);
  const addAccount = () => {
    setShowRegisterSocial(true);
  };
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshProfile = () => {
    setIsRefreshing(true);
    axios.post(`https://api.viralgpt.com/auth/refresh`, {
      profileid: profile.selectedProfile
      }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`
      }
    })
	.then(response => {
		console.log(response.data);
		setIsRefreshing(false);
    setFunctionSuccess(L.magnet.profileRefreshed);
    // small delay to show the message
    setTimeout(() => {
      window.location.reload();
    }, 1500);
	})
	.catch(error => {
		console.error("Error checking private account:", error);
    setFunctionError(error.response?.data?.error || error.response?.data?.message || L.magnet.failedRefreshProfile);
		setIsRefreshing(false);
	});
  };
  


  const profileSwitcher = () => {
    return (
      <Dropdown >
		<button 
			onClick={refreshProfile}
			className="absolute top-0 right-0 p-1 rounded-full"
			style={{background: 'transparent', border: 'none', cursor: 'pointer', color: '#8898aa', transition: 'color 0.15s ease'}}
			onMouseEnter={e => e.currentTarget.style.color = '#635bff'}
			onMouseLeave={e => e.currentTarget.style.color = '#8898aa'}
		>
			<i className={`fas fa-sync-alt ${isRefreshing ? 'fa-spin' : ''}`}></i>
		</button>
        <Dropdown.Toggle variant="link" className="p-0 text-decoration-none w-100" style={{caretColor: "transparent"}}>
          <div className="flex items-center space-x-4">
			
		  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" className="w-6 h-6 text-gray-500"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"></path></svg>
            <div className="flex-shrink-0">
				
              <div className="relative">
                <div className="relative flex flex-col items-center space-y-1">
                  <div className="background-animate rounded-full bg-gradient-to-r p-[0.17rem] from-fuchsia-500/80 via-red-600/50 to-orange-400/50">
                    <div className="block p-1 transition transform bg-white rounded-full">
                      <img 
                        className="h-[60px] w-[60px] rounded-full"
                        src={photo}
                        onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.png'; }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="my-auto">
              <div className="flex items-center">
                <h1 className="inline text-[21px] font-bold font-dashboard tracking-tight items-center" style={{color: '#0a2540'}}>
                  {platform === "x" ? (
                    <span className="bg-black px-1 rounded-md text-white">𝕏</span>
                  ) : (
                    <i className={`fa-brands fa-${platform} text-${platform}-dark`}></i>
                  )}
                  <span className="ml-2 ">{username.length > 16 ? `${username.slice(0, 16)}...` : username}</span>
                </h1>
              </div>
              <div className="flex gap-2">
                {profile?.socials?.[profile?.selectedProfile]?.planName === "pro" && (
                  <span className="flex items-center rounded-lg px-2 py-1 text-xs font-semibold tracking-normal text-white w-fit bg-instagram" style={{border: 'none'}}>
                    <i className="fas fa-crown mr-1"></i>
                    PRO
                  </span>
                )}
                {profile?.socials?.[profile?.selectedProfile]?.planName === "basic" && (
                  <span className="flex items-center rounded-lg px-2 py-1 text-xs font-semibold tracking-normal w-fit" style={{background: '#0a2540', color: '#fff', border: 'none'}}>
                    BASIC
                  </span>
                )}
				{profile?.socials?.[profile?.selectedProfile]?.planName === "starter" && (
                  <span className="flex items-center rounded-lg px-2 py-1 text-xs font-semibold tracking-normal w-fit" style={{background: '#8898aa', color: '#fff', border: 'none'}}>
                    STARTER
                  </span>
                )}
                <span className="flex items-center rounded-lg px-2 py-1 text-xs font-semibold tracking-normal text-white w-fit" style={{
                  background: (growthRate === "0" || isPaused || profile?.socials?.[profile?.selectedProfile]?.isPrivate) ? '#8898aa' : '#1a7f4b',
                  border: 'none',
                }}>
                  <i className={`fas fa-${growthRate === "0" || isPaused || profile?.socials?.[profile?.selectedProfile]?.isPrivate ? "pause" : "signal animate-pulse"} mr-1`}></i>
                  <div className="flex items-center">
                    {username === '' ? (
                      <>{L.magnet.statusNoAccount}</>
                    ) : profile?.socials?.[profile?.selectedProfile]?.isPrivate ? (
                      <>{L.magnet.statusPausedPrivate}</>
                    ) : isPaused ? L.magnet.statusGrowthPaused : growthRate === "0" ? L.magnet.noPlan : L.magnet.statusActive}
                  </div>
                </span>
              </div>
            </div>
			
          </div>
        </Dropdown.Toggle>

        <Dropdown.Menu className="w-100 mt-4">
          {profile?.socials && Object.entries(profile.socials).map(([key, social]) => (
            <Dropdown.Item 
              key={key}
              className={`d-flex align-items-center w-100 ${social.profileid === selectedProfile ? 'bg-light' : ''}`}
              onClick={() => selectProfile(social.profileid)}
            >
              <img 
                src={social.photo || defaultPhoto}
                alt="Profile"
                className="rounded-circle me-2"
                style={{width: "30px", height: "30px"}}
                onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.png'; }}
              />
			   <div className="w-6">
				{social.platform === 'x' ? (

					<span className="bg-black rounded-md text-white"><i className={`fa-brands fa-twitter-x text-${social.platform || platform}`}></i></span>
					

				) : (
					<i className={`fa-brands fa-${social.platform || platform} text-${social.platform || platform} me-1`}></i>
				)}
			  </div>
			  
              {social.username.length > 16 ? `${social.username.slice(0, 16)}...` : social.username}
              <div className="ms-auto d-flex align-items-center">
                <span className={`badge ${social.planName === 'pro' ? 'bg-instagram' : ''}`} style={{
                  background: social.planName === 'pro' ? undefined : social.planName === 'basic' ? '#0a2540' : social.planName === 'starter' ? '#8898aa' : '#e3e8ee',
                  color: social.planName ? '#fff' : '#425466',
                  fontSize: '0.65rem', fontWeight: 600, borderRadius: '6px', padding: '0.3em 0.6em',
                }}>
                  {(social.planName === 'pro' ? 'PRO' : social.planName === 'basic' ? 'BASIC' : social.planName === 'starter' ? 'STARTER' : L.magnet.noPlanBadge).toUpperCase()}
				</span>
				</div>
				<i className={`fas ${social.profileid === selectedProfile ? 'fa-check' : 'fa-check text-transparent'} ms-2`} style={{color: social.profileid === selectedProfile ? '#635bff' : 'transparent'}}></i>
			</Dropdown.Item>
          ))}
        </Dropdown.Menu>
		
		</Dropdown>
	);
};

  const interests = [
    { id: "global", label: `🌍 ${L.magnet.interests.global}` },
    { id: "fitness", label: `🏋️‍♂️ ${L.magnet.interests.fitness}` },
    { id: "travel", label: `✈️ ${L.magnet.interests.travel}` },
    { id: "fashion", label: `👗 ${L.magnet.interests.fashion}` },
    { id: "tech", label: `💻 ${L.magnet.interests.tech}` },
    { id: "food", label: `🍔 ${L.magnet.interests.food}` },
    { id: "music", label: `🎵 ${L.magnet.interests.music}` },
    { id: "art", label: `🎨 ${L.magnet.interests.art}` },
    { id: "gaming", label: `🎮 ${L.magnet.interests.gaming}` },
    { id: "photography", label: `📸 ${L.magnet.interests.photography}` },
    { id: "sports", label: `⚽ ${L.magnet.interests.sports}` },
    { id: "health", label: `🩺 ${L.magnet.interests.health}` },
    { id: "beauty", label: `💄 ${L.magnet.interests.beauty}` },
    { id: "books", label: `📚 ${L.magnet.interests.books}` },
    { id: "movies", label: `🎥 ${L.magnet.interests.movies}` },
    { id: "pets", label: `🐶 ${L.magnet.interests.pets}` },
    { id: "business", label: `💼 ${L.magnet.interests.business}` },
    { id: "realestate", label: `🏡 ${L.magnet.interests.realestate}` },
    { id: "homedecor", label: `🏠 ${L.magnet.interests.homedecor}` },
    { id: "education", label: `🎓 ${L.magnet.interests.education}` },
    { id: "development", label: `🌱 ${L.magnet.interests.development}` },
    { id: "wellness", label: `💊 ${L.magnet.interests.wellness}` },
    { id: "memes", label: `😂 ${L.magnet.interests.memes}` },
    { id: "nature", label: `🌳 ${L.magnet.interests.nature}` },
    { id: "cars", label: `🚗 ${L.magnet.interests.cars}` },
    { id: "writing", label: `✍️ ${L.magnet.interests.writing}` },
    { id: "news", label: `📰 ${L.magnet.interests.news}` },
    { id: "science", label: `🔬 ${L.magnet.interests.science}` },
    { id: "politics", label: `🗳️ ${L.magnet.interests.politics}` },
    { id: "history", label: `📜 ${L.magnet.interests.history}` }
  ];

  const countries = [
	{ id: "global", label: `🌍 ${L.magnet.countries.global}` },
	{ id: "us", label: `🇺🇸 ${L.magnet.countries.us}` },
	{ id: "ca", label: `🇨🇦 ${L.magnet.countries.ca}` },
	{ id: "mx", label: `🇲🇽 ${L.magnet.countries.mx}` },
	{ id: "gb", label: `🇬🇧 ${L.magnet.countries.gb}` },
	{ id: "au", label: `🇦🇺 ${L.magnet.countries.au}` },
	{ id: "nz", label: `🇳🇿 ${L.magnet.countries.nz}` },
	{ id: "de", label: `🇩🇪 ${L.magnet.countries.de}` },
	{ id: "fr", label: `🇫🇷 ${L.magnet.countries.fr}` },
	{ id: "es", label: `🇪🇸 ${L.magnet.countries.es}` },
	{ id: "it", label: `🇮🇹 ${L.magnet.countries.it}` },
	{ id: "nl", label: `🇳🇱 ${L.magnet.countries.nl}` },
	{ id: "be", label: `🇧🇪 ${L.magnet.countries.be}` },
	{ id: "se", label: `🇸🇪 ${L.magnet.countries.se}` },
	{ id: "no", label: `🇳🇴 ${L.magnet.countries.no}` },
	{ id: "dk", label: `🇩🇰 ${L.magnet.countries.dk}` },
	{ id: "ch", label: `🇨🇭 ${L.magnet.countries.ch}` },
	{ id: "at", label: `🇦🇹 ${L.magnet.countries.at}` },
	{ id: "pl", label: `🇵🇱 ${L.magnet.countries.pl}` },
	{ id: "pt", label: `🇵🇹 ${L.magnet.countries.pt}` },
	{ id: "gr", label: `🇬🇷 ${L.magnet.countries.gr}` },
	{ id: "ro", label: `🇷🇴 ${L.magnet.countries.ro}` },
	{ id: "bg", label: `🇧🇬 ${L.magnet.countries.bg}` },
	{ id: "hu", label: `🇭🇺 ${L.magnet.countries.hu}` },
	{ id: "cz", label: `🇨🇿 ${L.magnet.countries.cz}` },
	{ id: "br", label: `🇧🇷 ${L.magnet.countries.br}` },
	{ id: "ar", label: `🇦🇷 ${L.magnet.countries.ar}` },
	{ id: "cl", label: `🇨🇱 ${L.magnet.countries.cl}` },
	{ id: "co", label: `🇨🇴 ${L.magnet.countries.co}` },
	{ id: "pe", label: `🇵🇪 ${L.magnet.countries.pe}` },
	{ id: "ve", label: `🇻🇪 ${L.magnet.countries.ve}` },
	{ id: "ec", label: `🇪🇨 ${L.magnet.countries.ec}` },
	{ id: "uy", label: `🇺🇾 ${L.magnet.countries.uy}` },
	{ id: "py", label: `🇵🇾 ${L.magnet.countries.py}` },
	{ id: "bo", label: `🇧🇴 ${L.magnet.countries.bo}` },
	{ id: "cr", label: `🇨🇷 ${L.magnet.countries.cr}` },
	{ id: "pa", label: `🇵🇦 ${L.magnet.countries.pa}` },
	{ id: "do", label: `🇩🇴 ${L.magnet.countries.do}` },
	{ id: "gt", label: `🇬🇹 ${L.magnet.countries.gt}` },
	{ id: "sv", label: `🇸🇻 ${L.magnet.countries.sv}` },
	{ id: "hn", label: `🇭🇳 ${L.magnet.countries.hn}` },
	{ id: "ni", label: `🇳🇮 ${L.magnet.countries.ni}` }
	
	
	
  ]

  const emojis = [
    { symbol: "🔥", startX: -100, startY: -550, delay: 0 },
    { symbol: "🥳", startX: 120, startY: -550, delay: 0.5 },
    { symbol: "🤩", startX: -70, startY: -400, delay: 1 },
    { symbol: "✨", startX: 80, startY: -400, delay: 1.5 },
	{ symbol: "🔥", startX: -500, startY: -550, delay: 0 },
    { symbol: "🥳", startX: 520, startY: -550, delay: 0.5 },
    { symbol: "🤩", startX: -570, startY: -500, delay: 1 },
    { symbol: "✨", startX: 580, startY: -500, delay: 1.5 },
	{ symbol: "🔥", startX: -400, startY: -350, delay: 0 },
    { symbol: "🥳", startX: 320, startY: -400, delay: 0.5 },
    { symbol: "🤩", startX: -370, startY: -400, delay: 1 },
    { symbol: "✨", startX: 380, startY: -400, delay: 1.5 },
  ];

  const plans = [
	{
		name: "Pro Plan",
		url: "pro",
		price: { monthly: 199, annual: 99 },
		features: [
		  { icon: "fa-bolt", feature: "Instant Delivery", description: "Get your followers instantly after payment." },
		  { icon: "fa-user-group", feature: "7,500 - 10,000 Followers", description: "Get 7,500 - 10,000 followers per month." },
		  { icon: "fa-gift", feature: "Daily Bonuses", description: "Earn daily bonuses to gain more balance" },
		  { icon: "fa-coins", feature: "$500 Balance For Instant Boosts", description: "Use boost balance to send Likes, Views, and Comments to your posts." },
  
		  { icon: "fa-pencil", feature: "AI Caption Generator", description: "Generate captions for your posts using AI." },
		  { icon: "fa-lightbulb", feature: "Idea Generator", description: "Get ideas for your next post." },
		  { icon: "fa-user-check", feature: "Profile Enhancer", description: "Analyze your profile to get more followers." },
		  { icon: "fa-eye", feature: "Anonymous Story Viewer", description: "View stories anonymously." },
		  { icon: "fa-download", feature: "HQ Post Downloader", description: "Download high-quality posts." },
		],
		popular: true,
	  },
    {
      name: "Basic Plan",
	  url: "basic",
      price: { monthly: 59, annual: 29 }, // Separate pricing for monthly/annual
      features: [
		{ icon: "fa-bolt", feature: "Instant Delivery", description: "Get your followers instantly after payment." },
		{ icon: "fa-user-group", feature: "2,000 - 3,000 Followers", description: "Get 2,000 - 3,000 followers per month." },
		{ icon: "fa-coins", feature: "500 Boost Balance", description: "Use boost balance to send Likes, Views, and Comments to your posts." },
		{ icon: "fa-gift", feature: "Daily Bonuses", description: "Earn daily bonuses to gain more balance" },
      ],
      popular: false,
    }
  ];

  // Initialize default interests, age range, and gender only once the profile is available
  useEffect(() => {
    if (profile) {
      const defaultInterests = profile?.socials?.[profile?.selectedProfile]?.interests?.split(",") || ["global"];
      const defaultAgeRange = profile?.socials?.[profile?.selectedProfile]?.ageRange?.split("-") || [18, 65];
      const defaultGender = profile?.socials?.[profile?.selectedProfile]?.genderTarget || "both";
      const defaultCountry = profile?.socials?.[profile?.selectedProfile]?.countries?.split(",") || ["global"];

      setSelectedInterests(defaultInterests);
	  setSelectedCountries(defaultCountry);
      setAgeRange(defaultAgeRange);
      setGender(defaultGender);
      setUsername(profile?.socials?.[profile?.selectedProfile]?.username || ""); // Make sure we have Instagram username
    }
  }, [profile]);

  const handleCountryClick = (id) => {
    if (selectedCountries?.includes(id)) {
      setSelectedCountries(selectedCountries.filter((country) => country !== id));
    } else {
      setSelectedCountries([...selectedCountries, id]);
    }
  };


  const handleInterestClick = (id) => {
    if (selectedInterests?.includes(id)) {
      setSelectedInterests(selectedInterests.filter((interest) => interest !== id));
    } else {
      setSelectedInterests([...selectedInterests, id]);
    }
  };
  
  const handleSliderChange = (values) => {
    userEditedRef.current = true;
    setAgeRange(values);
  };

  useEffect(() => {
    // Skip the mount-time run (and the post-hydration re-run from the [profile]
    // init effect). Only a genuine user edit arms userEditedRef, so the blank
    // default payload is never POSTed in any timing order.
    if (!userEditedRef.current) return;
    const saveSettings = async () => {
      try {
        await axios.post(
          "https://api.viralgpt.com/auth/profile/update",
          {
            interests: selectedInterests.join(","),
            ageRange: ageRange.join("-"), 
            profileid: profile.selectedProfile,
            countries: selectedCountries.join(","),
            genderTarget: gender,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );

        
       

      } catch (e) {
        alert(L.magnet.failedSaveSettings(e.response.data.error));
      }
    };

    // Only call saveSettings if we have a selected profile
    if (profile?.selectedProfile) {
      saveSettings();
    }

  }, [selectedCountries, ageRange, selectedInterests, gender]);
  return (
	<div style={{background: 'linear-gradient(180deg, #f6f9fc 0%, #fff 100%)', minHeight: '100vh'}}>

		<Modal show={showRegisterSocial} onHide={() => setShowRegisterSocial(false)} centered size="lg">	
			<Modal.Header closeButton style={{border: 'none', padding: '1.25rem 1.5rem 0.5rem'}}>	
				<Modal.Title style={{fontWeight: 700, color: '#0a2540', fontSize: '1.15rem'}}>{L.magnet.addAccount}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{padding: '0.5rem 1.5rem 1.5rem'}}>
				<RegisterSocial 
					show={showRegisterSocial}
					onHide={() => setShowRegisterSocial(false)}
				/>
			</Modal.Body>
		</Modal>
	
<Modal show={showProModal} onHide={handleCloseModal} centered size="xl">
  <Modal.Header closeButton style={{border: 'none', padding: '1.25rem 1.5rem 0.5rem'}}>
    <Modal.Title>
      <span style={{fontWeight: 700, color: '#fff', padding: '0.3rem 0.9rem', borderRadius: '8px', background: 'linear-gradient(135deg, #635bff 0%, #7c3aed 100%)', fontSize: '0.95rem'}}>{L.magnet.comparePlans}</span>
    </Modal.Title>
  </Modal.Header>
  <Modal.Body style={{padding: '0.5rem 1.5rem 1.5rem'}}>
	<Upgrade/>
  </Modal.Body>
</Modal>

<Modal show={showUpsellModal} onHide={handleCloseUpsellModal} centered size="lg">
  <Modal.Header closeButton style={{border: 'none', padding: '1.25rem 1.5rem 0.5rem'}}>
    <Modal.Title>
      <span style={{fontWeight: 700, color: '#fff', padding: '0.3rem 0.9rem', borderRadius: '8px', background: 'linear-gradient(135deg, #635bff 0%, #7c3aed 100%)', fontSize: '0.95rem'}}>{L.magnet.manageAddons}</span>
    </Modal.Title>
  </Modal.Header>
  <Modal.Body style={{padding: '0.5rem 1.5rem 1.5rem'}}>
	<Upsell profile={profile} rank={profile?.socials?.[profile?.selectedProfile]} />
  </Modal.Body>
</Modal>

    <Container className="py-4 justify-content-center text-center align-items-center" style={{maxWidth: '1200px'}}>
	{profile?.invoices?.filter(invoice => invoice.status !== 'paid').length > 0 && (
		<div className="text-center" style={{background: '#fff0f0', border: '1px solid #fecdd3', borderRadius: '12px', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#e11d48', fontSize: '0.9rem', fontWeight: 500}}>
			<i className="fas fa-exclamation-triangle me-2"></i>
			{L.magnet.outstandingInvoice} <a href="/dashboard/settings?tab=invoices" style={{color: '#e11d48', textDecoration: 'underline', fontWeight: 600}}>{L.magnet.payYourInvoice}</a> {L.magnet.toContinue}
		</div>
	)}
	<Tab.Container activeKey={currentKey}>
        {/* A single-tab bar is just a label taking up space, so it only renders
            when there is actually a choice to make. */}
        {validModes.length > 1 && (
        <div className="my-4 mb-4" style={{overflowX: 'auto', WebkitOverflowScrolling: 'touch'}}>
          <div className="d-inline-flex w-100" style={{background: '#fff', borderRadius: '14px', padding: '5px', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)', gap: '4px'}}>
            {[
              {key: 'overview', icon: 'fa-magnet', label: L.magnet.tabMagnet},
              {key: 'boost', icon: 'fa-rocket', label: L.magnet.tabExtraBoosts},
              {key: 'tools', icon: 'fa-microchip', label: L.magnet.tabAiTools},
            ].filter((tab) => validModes.includes(tab.key)).map((tab) => (
              <button
                key={tab.key}
                onClick={() => changeTab(tab.key)}
                className="flex-1 d-flex align-items-center justify-content-center"
                style={{
                  padding: '0.6rem 1rem', borderRadius: '10px', border: 'none', cursor: 'pointer',
                  fontSize: '0.9rem', fontWeight: currentKey === tab.key ? 600 : 500, whiteSpace: 'nowrap',
                  color: currentKey === tab.key ? '#fff' : '#425466', gap: '0.5rem',
                  background: currentKey === tab.key ? 'linear-gradient(135deg, #635bff 0%, #7c3aed 100%)' : 'transparent',
                  boxShadow: currentKey === tab.key ? '0 4px 6px rgba(50,50,93,0.11), 0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                <i className={`fas ${tab.icon}`} style={{fontSize: '0.8rem'}}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        )}

        <Tab.Content>
          <Tab.Pane eventKey="boost" className="text-left">
            <Booster/>
            {/* The Growth/Expert membership offer used to sit here, shown once
                `membershipEligible` came back true. It is no longer pitched.
                components/Membership.js and config/memberships.js are kept —
                as is the server's grant path — so nothing breaks for the
                members already on a plan and the offer can be restored by
                rendering it again. */}
          </Tab.Pane>
          <Tab.Pane eventKey="tools" className="text-left">
            <Tools/>
          </Tab.Pane>
          <Tab.Pane eventKey="overview" className="text-left">

            <Alert 
              variant="success" 
              message={functionSuccess} 
              show={!!functionSuccess} 
              onClose={() => setFunctionSuccess("")} 
            />

	<div className="text-center justify-content-center align-items-center">
      
        <>
          {/* Main container with flex for larger screens */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
            
            {/* Instagram Profile Section */}
            <div className="xl:col-span-1">
              <div className="p-3 items-center bg-white" style={{borderRadius: '16px', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)'}}>
                {profileSwitcher()}
              </div>
            </div>

			<div className="xl:col-span-1 md:col-span-1 xl:col-start-3 md:col-start-2">
              <div className="bg-white p-3 flex flex-col justify-center w-full" style={{borderRadius: '16px', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)'}}>
                {lastMonthChange < 1 ? (
                  <div className="px-3 mb-2 text-center py-2" style={{borderRadius: '10px', background: '#f0ebff', border: '1px solid rgba(99,91,255,0.12)', fontSize: '0.75rem', color: '#425466'}}>
                   <i className="fas fa-cog fa-spin mr-1" style={{color: '#635bff'}}></i>
                    <span className="font-medium">{L.magnet.learningPhase}</span> {L.magnet.learningPhaseStart}
                    
                  </div>
                ) : lastMonthChange < 500 && (
                  <div className="px-3 mb-2 text-center py-2" style={{borderRadius: '10px', background: '#f0ebff', border: '1px solid rgba(99,91,255,0.12)', fontSize: '0.75rem', color: '#425466'}}>
                   <i className="fas fa-cog fa-spin mr-1" style={{color: '#635bff'}}></i>
                    <span className="font-medium">{L.magnet.learningPhase}</span> {L.magnet.learningPhaseTuning}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2 w-full">
                  {[
                    { label: "24H", value: lastDayChange, tooltip: L.magnet.gained24h },
                    { label: "1W", value: lastWeekChange, tooltip: L.magnet.gained7d },
                    { label: "1M", value: lastMonthChange, tooltip: L.magnet.gained30d }
                  ].map((period, index) => (
                    <OverlayTrigger
                      key={index}
                      placement="top"
                      overlay={<Tooltip>{period.tooltip}</Tooltip>}
                    >
                      <div className="p-2 text-base flex flex-col justify-center items-center w-full cursor-help" style={{background: '#f6f9fc', borderRadius: '12px', border: '1px solid #e3e8ee'}}>
                        <div className="font-bold mb-1 text-lg" style={{color: '#1a7f4b'}}>+{profile ? (period.value.toLocaleString()) : "..."}</div>
                        <div className="text-sm font-semibold" style={{color: '#0a2540'}}>{period.label}</div>
                      </div>
                    </OverlayTrigger>
                  ))}
                </div>
				<div className="mt-2" style={{fontSize: '0.7rem', textAlign: 'center', color: '#8898aa'}}>{L.magnet.refillPrompt} <a href="/dashboard/settings?tab=refills" style={{color: '#635bff'}}> {L.magnet.here}</a></div>
              </div>
            </div>

            {/* Active Plan Section */}
            <div className="xl:col-span-1 md:col-span-2 xl:col-start-2 xl:row-start-1 md:col-start-1">
              <div className="bg-white rounded-xl flex flex-col w-full overflow-hidden" style={{border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)'}}>

                {/* Plan hero — centered, compact */}
                <div className="flex flex-col items-center text-center py-3 px-3" style={{
                  background: growthRate !== "0" ? 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #f0f9ff 100%)' : '#f9fafb'
                }}>
                  {/* Plan icon */}
                  <div className={`flex items-center justify-center mb-1.5 ${growthRate === "100" ? 'bg-instagram' : ''}`} style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    ...( growthRate === "100" ? {} 
                      : growthRate !== "0" ? {background: '#0a2540'} : {background: '#d1d5db'}),
                  }}>
                    <i className={`fas ${growthRate === "100" ? 'fa-crown' : growthRate !== "0" ? 'fa-bolt' : 'fa-minus'}`} 
                      style={{color: '#fff', fontSize: '0.75rem'}}></i>
                  </div>
                  {/* Plan name + badge */}
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span style={{fontWeight: 700, fontSize: '1rem', color: '#0a2540', lineHeight: 1.2}}>
                      {growthRate === "100" ? "Pro" : growthRate === "50" ? "Basic" : growthRate === "7" ? "Starter" : L.magnet.noPlan}
                    </span>
                    {growthRate !== "0" && (
                      <span className="flex items-center gap-1" style={{
                        background: '#dcfce7', color: '#16a34a', fontSize: '0.6rem', fontWeight: 700,
                        padding: '1px 6px', borderRadius: '4px',
                      }}>
                        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{background: '#16a34a', display: 'inline-block'}}></span>
                        {L.magnet.activeBadge}
                      </span>
                    )}
                  </div>
                  {/* Rate */}
                  <div style={{fontSize: '0.78rem', color: '#64748b', fontWeight: 500}}>
                    {growthRate === "0" ? L.magnet.noActiveSubscription : (
                      <>{L.magnet.ratePerDay(growthRate === "100" ? "100" : growthRate === "50" ? "50" : "7", platform === "youtube" ? L.magnet.subs : L.magnet.followersWord)}</>
                    )}
                  </div>
                </div>

                {/* Primary CTAs side-by-side: Upgrade/Start + Boost */}
                <div className="px-3 pt-2.5 flex gap-2">
                  {(growthRate === "0" || growthRate !== "100") && (
                    <button
                      onClick={handleProClick}
                      className="flex-1 py-2.5 font-bold text-white text-sm flex items-center justify-center gap-1.5 transition-all duration-200 bg-instagram"
                      style={{ border: 'none', borderRadius: '10px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
                    >
                      {growthRate === "0" ? (
                        <><i className="fas fa-rocket" style={{fontSize: '0.65rem'}}></i>{L.magnet.startGrowing}</>
                      ) : (
                        <><i className="fas fa-arrow-up" style={{fontSize: '0.6rem'}}></i>{L.magnet.upgradePlan}</>
                      )}
                    </button>
                  )}
                  <button
                    onClick={handleShowUpsellModal}
                    className="flex-1 py-2.5 font-bold text-white text-sm flex items-center justify-center gap-1.5 transition-all duration-200 hover:shadow-xl"
                    style={{
                      background: 'linear-gradient(135deg, #635bff 0%, #9854ff 50%, #d345c6 100%)',
                      border: 'none', borderRadius: '10px', cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(99,91,255,0.30)',
                    }}
                  >
                    <i className="fas fa-bolt" style={{fontSize: '0.65rem'}}></i>
                    {L.magnet.boostGrowth}
                  </button>
                </div>

                {/* Pause/Resume — tertiary text-only control */}
                {growthRate !== "0" && (
                  <div className="text-center py-2.5">
                    <button
                      onClick={handleTogglePause}
                      disabled={isPauseLoading}
                      className="text-xs font-medium transition-colors duration-200"
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: isPaused ? '#059669' : '#94a3b8',
                      }}
                    >
                      {isPauseLoading ? (
                        <><i className="fas fa-spinner fa-spin mr-1.5" style={{fontSize: '0.6rem'}}></i>{L.magnet.updating}</>
                      ) : isPaused ? (
                        <><i className="fas fa-play mr-1.5" style={{fontSize: '0.55rem'}}></i>{L.magnet.resumeGrowthLink}</>
                      ) : (
                        <><i className="fas fa-pause mr-1.5" style={{fontSize: '0.55rem'}}></i>{L.magnet.pauseGrowthLink}</>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Follower Counts Section */}
            

          </div>
        </>
  
    </div>

	
	
		<div className="p-4 mt-4" style={{background: '#fff', borderRadius: '16px', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)'}}>
              <h5 style={{fontWeight: 700, color: '#0a2540', fontSize: '1.1rem', marginBottom: '0.25rem'}}>{L.magnet.projectedGrowth}</h5>
              <p style={{color: '#425466', fontSize: '0.9rem', marginBottom: '1.25rem'}}>{L.magnet.projection(growthRate != "0", (followers + ((growthRate === "0" ? "100" : growthRate) * days)).toLocaleString(), platform === "youtube" ? L.magnet.subscribers : L.magnet.followersWord, days > 30 ? L.magnet.monthsCount(Math.round(days/30)) : L.magnet.daysCount(days))}</p>

              <div className="w-100">
                <ChartComponent followers={followers} growthRate={growthRate === "0" ? "100" : growthRate} days={days}/>
                
                <div className="flex justify-center mt-6">
                  <div className="d-inline-flex" style={{background: '#f6f9fc', borderRadius: '10px', padding: '4px', border: '1px solid #e3e8ee', gap: '3px'}}>
                    {[{d: 30, l: '1M'}, {d: 180, l: '6M'}, {d: 360, l: '1Y'}].map(t => (
                      <button key={t.d} onClick={() => setDays(t.d)} style={{
                        padding: '0.5rem 1.25rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
                        fontSize: '0.85rem', fontWeight: days === t.d ? 600 : 500,
                        color: days === t.d ? '#fff' : '#425466',
                        background: days === t.d ? 'linear-gradient(135deg, #635bff 0%, #7c3aed 100%)' : 'transparent',
                        boxShadow: days === t.d ? '0 2px 4px rgba(99,91,255,0.2)' : 'none',
                        transition: 'all 0.2s ease',
                      }}>{t.l}</button>
                    ))}
                  </div>
                </div>
              </div>
</div>
          
            <div className="mt-4 p-4 text-start" style={{background: '#fff', borderRadius: '16px', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)'}}>
              <h5 style={{fontWeight: 700, color: '#0a2540', fontSize: '1.1rem', marginBottom: '0.25rem'}}>{L.magnet.targetInterests}</h5>
              <p style={{color: '#425466', fontSize: '0.9rem', marginBottom: '1rem'}}>{L.magnet.targetInterestsDesc}</p>

              <Form.Group className="mb-4" style={{background: '#f6f9fc', padding: '0.75rem', borderRadius: '12px', border: '1px solid #e3e8ee'}}>
                <div className="d-flex flex-wrap gap-1">
                  {interests.map((interest) => (
                    <div
                      key={interest.id}
                      onClick={() => {
                        userEditedRef.current = true;
                        if (interest.id === "global") {
                          if (selectedInterests.includes("global")) {
                            handleInterestClick("global");
                          } else {
                            setSelectedInterests(["global"]);
                          }
                        } else {
                          handleInterestClick(interest.id);
                          const updatedInterests = selectedInterests.includes(interest.id) 
                            ? selectedInterests.filter(i => i !== interest.id)
                            : [...selectedInterests, interest.id];
                          if (updatedInterests.length === 0) {
                            setSelectedInterests(["global"]);
                          } else {
                            setSelectedInterests(updatedInterests.filter(i => i !== "global"));
                          }
                        }
                      }}
                      className="cursor-pointer"
                      style={{
                        padding: '0.4rem 0.75rem', margin: '3px', borderRadius: '8px', fontSize: '0.85rem',
                        fontWeight: selectedInterests?.includes(interest.id) ? 600 : 400,
                        background: selectedInterests?.includes(interest.id) ? 'linear-gradient(135deg, #635bff 0%, #7c3aed 100%)' : '#fff',
                        color: selectedInterests?.includes(interest.id) ? '#fff' : '#425466',
                        border: selectedInterests?.includes(interest.id) ? '1px solid transparent' : '1px solid #e3e8ee',
                        boxShadow: selectedInterests?.includes(interest.id) ? '0 2px 4px rgba(99,91,255,0.2)' : 'none',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {interest.label}
                    </div>
                  ))}
                </div>
              </Form.Group>
			</div>

			{SHOW_COUNTRY_TARGETING && (
			<div className="mt-4 p-4 text-start" style={{background: '#fff', borderRadius: '16px', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)'}}>
				<h5 style={{fontWeight: 700, color: '#0a2540', fontSize: '1.1rem', marginBottom: '0.25rem'}}>{L.magnet.targetCountries}</h5>
				<p style={{color: '#425466', fontSize: '0.9rem', marginBottom: '1rem'}}>{L.magnet.targetCountriesDesc(platform === "youtube" ? L.magnet.subscribers : L.magnet.followersWord)}</p>
				<Form.Group className="mb-4" style={{background: '#f6f9fc', padding: '0.75rem', borderRadius: '12px', border: '1px solid #e3e8ee'}}>
				<div className="d-flex flex-wrap gap-1">
					{countries.map((country) => (
						<div
							key={country.id}
							onClick={() => {
								userEditedRef.current = true;
								if (country.id === "global") {
									if (selectedCountries.includes("global")) {
										handleCountryClick("global");
									} else {
										setSelectedCountries(["global"]);
									}
								} else {
									handleCountryClick(country.id);
									const updatedCountries = selectedCountries.includes(country.id)
										? selectedCountries.filter(c => c !== country.id)
										: [...selectedCountries, country.id];
									if (updatedCountries.length === 0) {
										setSelectedCountries(["global"]);
									} else {
										setSelectedCountries(updatedCountries.filter(c => c !== "global"));
									}
								}
							}}
							className="cursor-pointer"
							style={{
								padding: '0.4rem 0.75rem', margin: '3px', borderRadius: '8px', fontSize: '0.85rem',
								fontWeight: selectedCountries?.includes(country.id) ? 600 : 400,
								background: selectedCountries?.includes(country.id) ? 'linear-gradient(135deg, #635bff 0%, #7c3aed 100%)' : '#fff',
								color: selectedCountries?.includes(country.id) ? '#fff' : '#425466',
								border: selectedCountries?.includes(country.id) ? '1px solid transparent' : '1px solid #e3e8ee',
								boxShadow: selectedCountries?.includes(country.id) ? '0 2px 4px rgba(99,91,255,0.2)' : 'none',
								transition: 'all 0.15s ease',
							}}
						>
							{country.label}
						</div>
					))}
				</div>
				</Form.Group>
			</div>
			)}
			<div className="mt-4 p-4 text-start" style={{background: '#fff', borderRadius: '16px', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)'}}>
				<h5 style={{fontWeight: 700, color: '#0a2540', fontSize: '1.1rem', marginBottom: '0.25rem'}}>{L.magnet.targetDemographics}</h5>
				<p style={{color: '#425466', fontSize: '0.9rem', marginBottom: '1rem'}}>{L.magnet.targetDemoDesc(platform === "youtube" ? L.magnet.subscribers : L.magnet.followersWord)}</p>

				<Form.Group className="mb-4">	
              <p style={{fontWeight: 600, color: '#0a2540', fontSize: '0.9rem'}}>{L.magnet.yearsRange(ageRange[0], ageRange[1])}</p>
              <ReactSlider
                className="custom-slider"
                thumbClassName="custom-thumb"
                trackClassName="custom-track"
                min={13}
                max={100}
                value={ageRange}
                onChange={handleSliderChange}
                pearling
                minDistance={1}
                renderTrack={(props, state) => (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      background:
                        state.index === 0
                          ? "#e3e8ee"
                          : state.index === 1
                          ? "linear-gradient(135deg, #635bff 0%, #7c3aed 100%)"
                          : "#e3e8ee",
                    }}
                  />
                )}
              />
			</Form.Group>
			<div style={{marginBottom: '1.25rem'}}></div>
			
			<Form.Group className="mb-4" style={{background: '#f6f9fc', padding: '0.75rem', borderRadius: '12px', border: '1px solid #e3e8ee'}}>	
              <div className="d-flex flex-wrap justify-content-center gap-2">
                {[
                  {value: 'male', emoji: '👨', label: L.magnet.genderMale, desc: L.magnet.maleDesc(platform === "youtube" ? L.magnet.subscribers : L.magnet.followersWord)},
                  {value: 'female', emoji: '👩', label: L.magnet.genderFemale, desc: L.magnet.femaleDesc(platform === "youtube" ? L.magnet.subscribers : L.magnet.followersWord)},
                  {value: 'both', emoji: '👫', label: L.magnet.genderBoth, desc: L.magnet.bothDesc},
                ].map(g => (
                  <div
                    key={g.value}
                    onClick={() => { userEditedRef.current = true; setGender(g.value); }}
                    className="d-flex align-items-center flex-grow-1 cursor-pointer"
                    style={{
                      padding: '0.6rem 0.75rem', margin: '3px', borderRadius: '12px',
                      background: gender === g.value ? '#fff' : '#fff',
                      border: gender === g.value ? '2px solid #635bff' : '1px solid #e3e8ee',
                      boxShadow: gender === g.value ? '0 2px 8px rgba(99,91,255,0.15)' : 'none',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <span style={{fontSize: "2.5em", marginRight: "0.75rem"}}>{g.emoji}</span>
                    <div>
                      <div style={{fontWeight: 700, color: '#0a2540', fontSize: '0.9rem'}}>{g.label}</div>
                      <small style={{color: '#8898aa', fontSize: '0.75rem'}}>{g.desc}</small>
                    </div>
                  </div>
                ))}
              </div>
			  </Form.Group>
            </div>
       
			</Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>

    {/* Pause Confirmation Modal */}
    <Modal show={showPauseConfirm} onHide={() => setShowPauseConfirm(false)} centered size="sm">
      <Modal.Body className="text-center p-4">
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
          <i className="fa-solid fa-pause" style={{ color: '#d97706', fontSize: '1rem' }}></i>
        </div>
        <h5 className="stripe-heading stripe-heading--xs" style={{ marginBottom: 8 }}>{L.magnet.pauseConfirmTitle}</h5>
        <p className="stripe-body stripe-body--sm stripe-text-muted" style={{ marginBottom: 20 }}>
          {L.magnet.pauseConfirmBody}
        </p>
        <div className="d-flex gap-2 justify-content-center">
          <Button
            variant="light"
            onClick={() => setShowPauseConfirm(false)}
            style={{ borderRadius: 8, padding: '0.5rem 1.25rem', fontWeight: 600, fontSize: '0.85rem' }}
          >
            {L.magnet.cancel}
          </Button>
          <Button
            onClick={executePauseToggle}
            style={{ borderRadius: 8, padding: '0.5rem 1.25rem', fontWeight: 600, fontSize: '0.85rem', background: '#d97706', border: 'none', color: '#fff' }}
          >
            {L.magnet.yesPause}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  </div>
  );
};

export default Magnet;
